import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table";
import { TooltipProvider} from "@/components/ui/tooltip";
import { CheckCircle, XCircle, Clock, Filter, Search, Users, Eye, User, ClipboardList, Send, MessageSquareDot, X, ChevronRight, MoreHorizontal, CheckCircle2, Ban, Info, Hash, Calendar, MessageSquare, File, Pencil} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";
import { adminApproveRejectLeave, adminApplyLeave, fetchAdminLeaveDashboard, viewDocument, fetchAllLeaveRequests, cancelLeaveRequest, fetchEmployeeEncashmentRequests, approveLeaveEncashmentRequest, rejectLeaveEncashmentRequest, adminUpdateLeave,
} from "@/services/leaveService";
import ApplyLeaveDialog from "@/components/common/LeaveRequestDialog";
import { Label } from "@/components/ui/label";
import { PaginationPageSize } from "@/components/ui/pagination";
import { DatePickerInput } from "@/components/ui/DatePickerInput";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const getStatusBadge = (status) => {
  const display = String(status || "").toLowerCase();

  const base = "capitalize rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide border";

  const styles = {
    pending: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200",
    approved: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200",
    completed: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200",
    rejected: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200",
    cancelled: "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200",
  };

  return (
    <Badge variant="outline" className={`${base} ${styles[display] || "border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-900/20 dark:text-gray-200"}`}>
      {display}
    </Badge>
  );
};

// Pending requests also show which approval step they're at: the employee's
// manager first (when they can approve leave), then an admin who can approve leave.
const getLeaveStatus = (request) => (
  <div className="inline-flex flex-col items-start gap-1">
    {getStatusBadge(request?.status)}
    {request?.status === "pending" && request?.approval_stage && (
      <span className="text-[11px] text-muted-foreground">
        {request.approval_stage === "manager"
          ? `Awaiting ${request.awaiting_manager?.name || "manager"}`
          : "Awaiting admin"}
      </span>
    )}
  </div>
);

const LeaveManagement = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaveStats, setLeaveStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedEncashmentRequest, setSelectedEncashmentRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [approvalComment, setApprovalComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [showAdminApplyDialog, setShowAdminApplyDialog] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Leave table pagination
  const [leavePage, setLeavePage] = useState(1);

  // Encashment table pagination
  const [encashPage, setEncashPage] = useState(1);

  const handlePageSizeChange = (value) => {
    setItemsPerPage(Number(value));
    setLeavePage(1);
    setEncashPage(1);
  };

  const [adminSubmitting, setAdminSubmitting] = useState(false);
  const [workingDaysConfig, setWorkingDaysConfig] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellingLeave, setCancellingLeave] = useState(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [leaveConfig, setLeaveConfig] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({
    leave_id: "",
    half_day: false,
    half_day_start_time: "",
    half_day_end_time: "",
    admin_comment: "",
  });

  const [encashmentRequest, setEncashmentRequest] = useState([]);
  const [approveEncashDialogOpen, setApproveEncashDialogOpen] = useState(false);
  const [rejectEncashDialogOpen, setRejectEncashDialogOpen] = useState(false);
  const [encashRejectionReason, setEncashRejectionReason] = useState("");
  const [payoutDay, setPayoutDay] = useState("");
  const { can } = usePermission();
  const { canCreate, canEdit } = createCanHelpers(can);
  const canCreateLeave = canCreate("leave_management");
  const canEditLeave = canEdit("leave_management");
  const [showLeaveDetailsDialog, setShowLeaveDetailsDialog] = useState(false);

  useEffect(() => {
    fetchInitialData();
    getEmployeeEncashmentRequests();
  }, []);

  const filteredRequests = useMemo(() => {
    let filtered = [...leaveRequests];

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter((request) => {
        return (
          request.employee_id.toLowerCase().includes(lower) ||
          request.employee_name.toLowerCase().includes(lower) ||
          request.reason.toLowerCase().includes(lower) ||
          request.leave_type.toLowerCase().includes(lower)
        );
      });
    }

    if (statusFilter === "mine") {
      filtered = filtered.filter((request) => request.can_act);
    } else if (statusFilter !== "all") {
      filtered = filtered.filter((request) => request.status === statusFilter);
    }

    return filtered.sort((a, b) => {
      const dateA = new Date(a.applied_date || a.created_at || 0);
      const dateB = new Date(b.applied_date || b.created_at || 0);
      return dateB - dateA;
    });
  }, [leaveRequests, searchTerm, statusFilter]);

  const fetchInitialData = async () => {
    try {
      const [dashboard, leaveRequests] = await Promise.all([
        fetchAdminLeaveDashboard(),
        fetchAllLeaveRequests(),
      ]);
      setEmployees(dashboard.employees);
      setLeaveRequests(leaveRequests.leave_requests);
      setHolidays(dashboard.holidays);
      setWorkingDaysConfig(dashboard.workingDaysConfig);
      setLeaveStats(dashboard.entitlements);
      setLeaveConfig(dashboard.leave_config || []);
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const approveEncashmentRequest = async () => {
    if (!canEditLeave) return;
    if (!selectedEncashmentRequest?._id) return;
    try {
      setActionLoading(true);
      await approveLeaveEncashmentRequest({
        request_id: selectedEncashmentRequest._id,
        employee_id: selectedEncashmentRequest.employee_id,
        company_id: selectedEncashmentRequest.company_id,
        payoutDay,
      });
      toast.success("Leave encashment approved");
      getEmployeeEncashmentRequests();
      setApproveEncashDialogOpen(false);
    } catch (err) {
      toast.error("Failed to approve leave encashment");
    } finally {
      setActionLoading(false);
    }
  };

  const rejectEncashmentRequest = async () => {
    if (!canEditLeave) return;
    if (!selectedEncashmentRequest?._id || !encashRejectionReason) return;

    try {
      setActionLoading(true);
      await rejectLeaveEncashmentRequest({
        request_id: selectedEncashmentRequest._id,
        rejection_reason: encashRejectionReason,
      });
      toast.success("Leave encashment request rejected");
      setRejectEncashDialogOpen(false);
      setRejectionReason("");
    } catch (err) {
      toast.error("Failed to reject leave encashment");
    } finally {
      getEmployeeEncashmentRequests();
      setActionLoading(false);
    }
  };

  const getEmployeeEncashmentRequests = async () => {
    try {
      const response = await fetchEmployeeEncashmentRequests();
      console.log(response.data);

      setEncashmentRequest(response.data);
    } catch (err) {
      toast.error("Failed to load dashboard");
    }
  };

  const handleApproveReject = async (leaveId, status, comment = "") => {
    if (!canEditLeave) return;
    setActionLoading(true);
    try {
      const res = await adminApproveRejectLeave(leaveId, status, comment);

      toast.success(res?.message || `Leave ${status} successfully`);
      fetchInitialData();

      setApprovalComment("");
      setRejectionReason("");
      setShowApprovalDialog(false);
      setShowRejectionDialog(false);
      setSelectedRequest(null);
    } catch (err) {
      if (!isAuthExpiryError(err)) {
        toast.error(getErrorMessage(err, "Failed to update leave request status"));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const openApprovalDialog = (request) => {
    setSelectedRequest(request);
    setShowApprovalDialog(true);
    setApprovalComment(request.admin_comment || "");
  };

  const openRejectionDialog = (request) => {
    setSelectedRequest(request);
    setShowRejectionDialog(true);
    setRejectionReason("");
  };

  const viewLeaveDocument = async (key) => {
    try {
      const response = await viewDocument(key);
      const { url } = response;
      window.open(url, "_blank");
    } catch {
      toast.error("Failed to load document");
    }
  };

  const getStats = () => {
    const total = leaveRequests.length;
    const pending = leaveRequests.filter((r) => r.status === "pending").length;
    const approved = leaveRequests.filter(
      (r) => r.status === "approved",
    ).length;
    const rejected = leaveRequests.filter(
      (r) => r.status === "rejected" || r.status === "cancelled",
    ).length;

    return { total, pending, approved, rejected };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6 space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-10 w-full" />
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const canCancelLeave = (request) => {
    console.log("Checking if leave can be cancelled:", request);
    // Admin can remove any pending/approved leave — past or future. The backend
    // blocks only leaves whose month's payroll has already been finalised (and
    // surfaces that as an error), so no date restriction is needed here.
    return request.status === "pending" || request.status === "approved";
  };

  const openCancelDialog = (request) => {
    setCancellingLeave(request);
    setCancellationReason("");
    setShowCancelDialog(true);
  };

  const openEditDialog = (request) => {
    setEditingLeave(request);
    setEditForm({
      leave_id: request.leave_id ? String(request.leave_id) : "",
      half_day: Boolean(request.half_day),
      half_day_start_time: request.half_day_start_time || "",
      half_day_end_time: request.half_day_end_time || "",
      admin_comment: "",
    });
    setShowEditDialog(true);
  };

  const isSingleDayLeave = (request) =>
    request &&
    String(request.start_date).slice(0, 10) ===
      String(request.end_date).slice(0, 10);

  const editValidationError = (() => {
    if (!editForm.leave_id) return "Select a leave type.";
    if (!editForm.half_day) return "";
    if (!isSingleDayLeave(editingLeave))
      return "Half day can only be set on a single-day leave.";
    if (!editForm.half_day_start_time || !editForm.half_day_end_time)
      return "Enter the start and end time the employee will work.";
    const toMinutes = (v) => {
      const [h, m] = v.split(":").map(Number);
      return h * 60 + m;
    };
    if (
      toMinutes(editForm.half_day_end_time) <=
      toMinutes(editForm.half_day_start_time)
    )
      return "End time must be after the start time.";
    return "";
  })();

  const handleEditLeave = async () => {
    if (!canEditLeave || !editingLeave) return;
    if (editValidationError) {
      toast.error(editValidationError);
      return;
    }

    setEditSubmitting(true);
    try {
      await adminUpdateLeave(editingLeave._id || editingLeave.id, {
        leave_id: editForm.leave_id,
        half_day: editForm.half_day,
        half_day_start_time: editForm.half_day
          ? editForm.half_day_start_time
          : "",
        half_day_end_time: editForm.half_day ? editForm.half_day_end_time : "",
        admin_comment: editForm.admin_comment,
      });

      toast.success("Leave request updated");
      setShowEditDialog(false);
      setEditingLeave(null);
      fetchInitialData();
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to update leave request",
      );
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCancelLeave = async () => {
    if (!cancellationReason.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }
    const employeeId = { employeeId: cancellingLeave.employee_id };

    setCancelling(true);
    try {
      await cancelLeaveRequest(
        cancellingLeave._id,
        cancellationReason,
        employeeId,
      );
      fetchInitialData();

      toast.success("Leave request cancelled successfully");
      setShowCancelDialog(false);
      setCancellingLeave(null);
      setCancellationReason("");
    } catch (error) {
      console.error("Error cancelling leave:", error);
      toast.error(getErrorMessage(error, "Failed to cancel leave request"));
    } finally {
      setCancelling(false);
    }
  };

  const handleAdminLeaveSubmit = async (
    formData,
    calculatedDays,
    documentFile,
  ) => {
    setAdminSubmitting(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append("employee_id", formData.employee_id);
      formDataObj.append("leave_id", formData.leave_id);
      formDataObj.append("leave_name", formData.leave_name);
      formDataObj.append("start_date", formData.start_date);
      formDataObj.append("end_date", formData.end_date);
      formDataObj.append("reason", formData.reason);
      formDataObj.append("days", calculatedDays);
      formDataObj.append("half_day", formData.half_day ? "true" : "false");

      if (formData.half_day) {
        formDataObj.append(
          "half_day_start_time",
          formData.half_day_start_time || "",
        );
        formDataObj.append(
          "half_day_end_time",
          formData.half_day_end_time || "",
        );
      }

      if (documentFile) {
        formDataObj.append("document", documentFile);
      }

      const res = await adminApplyLeave(formDataObj);

      // Only an approving admin adds an already-approved leave; anyone else's becomes a
      // pending request that goes through the approval steps. The employee's
      // own manager approves it by applying, so theirs goes straight to the final step.
      const created = res?.data?.leave;
      toast.success(
        created?.status !== "pending"
          ? "Leave created successfully"
          : created?.manager_approval?.approved_at
            ? "Leave applied and sent to an admin for final approval."
            : "Leave request created — it now needs approval.",
      );
      fetchInitialData();
    } catch (err) {
      // Handle the error here (toast) and do NOT re-throw — the dialog's
      // fail-safe catch would otherwise toast the same message a second time.
      const errorMessage = getErrorMessage(err, "Failed to create leave request");
      toast.error(errorMessage);
    } finally {
      setAdminSubmitting(false);
      setShowAdminApplyDialog(false);
    }
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find((emp) => emp._id === employeeId);
    return employee ? employee.name : `[ID: ${employeeId}]`;
  };

  const getEmployeeId = (employeeId) => {
    const employee = employees.find((emp) => emp._id === employeeId);
    return employee ? employee.employee_id : "";
  };

  // pagination slice
  const leaveTotalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const visibleLeavePages = (() => {
    if (leaveTotalPages <= 7) {
      return Array.from({ length: leaveTotalPages }, (_, index) => index + 1);
    }

    if (leavePage <= 3) {
      return [1, 2, 3, "ellipsis-end", leaveTotalPages];
    }

    if (leavePage >= leaveTotalPages - 2) {
      return [1, "ellipsis-start", leaveTotalPages - 2, leaveTotalPages - 1, leaveTotalPages];
    }

    const sortedPages = [1, leavePage - 1, leavePage, leavePage + 1, leaveTotalPages];

    const items = [];

    sortedPages.forEach((page, index) => {
      const previousPage = sortedPages[index - 1];

      if (index > 0 && page - previousPage > 1) {
        items.push(`ellipsis-${previousPage}`);
      }

      items.push(page);
    });

    return items;
  })();

  const paginatedRequests = filteredRequests.slice(
    (leavePage - 1) * itemsPerPage,
    leavePage * itemsPerPage,
  );

  const encashTotalPages = Math.ceil(encashmentRequest.length / itemsPerPage);

  const paginatedEncashRequests = encashmentRequest.slice(
    (encashPage - 1) * itemsPerPage,
    encashPage * itemsPerPage,
  );

    const statusColor = {
      pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
      approved: "bg-green-500/10 text-green-600 border-green-500/20",
      rejected: "bg-red-500/10 text-red-600 border-red-500/20",
      cancelled: "bg-gray-500/10 text-gray-600 border-gray-500/20",
    };

    const InfoItem = ({ label, value }) => (
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-medium text-foreground">{value || "--"}</p>
      </div>
    );

    const NoteItem = ({ title, text }) => (
      <div className="bg-muted/30 p-3 rounded-lg border border-border m-1">
        <p className="text-xs text-muted-foreground mb-1">{title}</p>
        <p className="text-sm italic">{text}</p>
      </div>
    );

  return (
    <div className="space-y-8 mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center border-b pb-4">
        <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">
          Leave Management
        </h1>
        <Button
          disabled={!canCreateLeave}
          onClick={() => {
            if (!canCreateLeave) return;
            setShowAdminApplyDialog(true);
          }}
        >
          + Apply Leave for Employee
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-gray-500 uppercase">
              Total Requests
            </p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {stats.total}
              </p>
              <ClipboardList className="h-6 w-6 text-blue-500 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-gray-500 uppercase">
              Pending
            </p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                {stats.pending}
              </p>
              <Clock className="h-6 w-6 text-yellow-500 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-gray-500 uppercase">
              Approved
            </p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                {stats.approved}
              </p>
              <CheckCircle className="h-6 w-6 text-green-500 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-gray-500 uppercase">
              Rejected / Cancelled
            </p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                {stats.rejected}
              </p>
              <XCircle className="h-6 w-6 text-red-500 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* TooltipProvider wraps entire table */}
      <TooltipProvider delayDuration={300}>
        <Card>
          <CardHeader className="px-6 py-4 dark:border-gray-700">
            <CardTitle className="text-xl">All Leave Applications</CardTitle>

            {/* Search + Filter */}
            <div className="flex flex-col md:flex-row gap-3 items-center pt-4">
              <div className="flex-1 relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search employee, reason, or leave type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full"
                />
              </div>
              <div className="flex items-center space-x-2 w-full md:w-auto">
                <Filter className="h-4 w-4 text-gray-500" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-[150px]">
                    <SelectValue placeholder="Filter Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="mine">Needs my approval</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="overflow-x-auto">
              <div className="border border-border rounded-lg overflow-hidden">
                  <Table className="w-full">
                    <TableHeader>
                      <TableRow className="border-b border-border">
                        <TableHead className="text-muted-foreground min-w-[220px]">
                          Employee
                        </TableHead>

                        <TableHead className="text-muted-foreground min-w-[170px]">
                          Leave
                        </TableHead>

                        <TableHead className="text-muted-foreground min-w-[220px]">
                          Dates
                        </TableHead>

                        <TableHead className="text-muted-foreground w-[150px]">
                          Status
                        </TableHead>

                        <TableHead className="text-muted-foreground text-center w-[190px]">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {paginatedRequests.map((request, idx) => {
                        const isRange = request.start_date !== request.end_date;
                        return (
                          <TableRow
                            key={request.id}
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowLeaveDetailsDialog(true);
                            }}
                            className={[
                              "border-b border-border",
                              "transition-colors cursor-pointer",
                              "hover:bg-muted/50",
                            ].join(" ")}
                          >
                            {/* Employee */}
                            <TableCell className="align-middle">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="min-w-0">
                                  <div className="font-semibold text-sm text-foreground truncate">
                                    {request.employee_name}
                                  </div>
                                  <div className="text-xs text-muted-foreground truncate">
                                    {request.emp_id}
                                  </div>
                                </div>
                              </div>
                            </TableCell>

                            {/* Leave (Type + Days merged) */}
                            <TableCell className="align-middle">
                              <div className="flex items-center gap-2">
                                <div className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-semibold text-foreground">
                                  {request.leave_type}
                                </div>

                                <div className="inline-flex items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-primary/20 px-3 py-1 text-xs font-semibold tabular-nums">
                                  {String(request.days).padStart(2, "0")}d
                                </div>
                              </div>
                            </TableCell>

                            {/* Dates (Period + Applied + Doc merged) */}
                            <TableCell className="align-middle">
                              <div className="flex flex-col gap-1">
                                <div className="text-sm font-medium text-foreground">
                                  {isRange ? (
                                    <>
                                      {format(
                                        new Date(request.start_date),
                                        "dd MMM",
                                      )}
                                      –
                                      {format(
                                        new Date(request.end_date),
                                        "dd MMM yyyy",
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {format(
                                        new Date(request.start_date),
                                        "dd MMM yyyy",
                                      )}
                                    </>
                                  )}
                                </div>

                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>
                                    Applied{" "}
                                    {format(
                                      new Date(request.applied_date),
                                      "dd MMM yyyy",
                                    )}
                                  </span>
                                </div>
                              </div>
                            </TableCell>

                            {/* Status (better capsule) */}
                            <TableCell className="align-middle">
                              <div className="inline-flex items-center gap-2 rounded-full ">
                                {getLeaveStatus(request)}
                              </div>
                            </TableCell>

                            {/* Actions (cleaner, consistent) */}
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-1.5">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 text-xs font-semibold text-muted-foreground hover:bg-muted transition-all flex items-center group/btn"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setShowLeaveDetailsDialog(true);
                                  }}
                                >
                                  <Eye className="w-3.5 h-3.5 mr-2 opacity-60 group-hover/btn:text-primary group-hover/btn:opacity-100 transition-all" />
                                  <span className="group-hover/btn:text-primary transition-colors">
                                    View
                                  </span>
                                  <ChevronRight className="w-3 h-3 ml-1 opacity-0 -translate-x-1 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 group-hover/btn:text-primary transition-all" />
                                </Button>

                                <div className="w-px h-4 bg-border mx-1" />

                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-muted-foreground hover:text-primary dark:hover:text-primary hover:bg-muted transition-all"
                                    >
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align="end"
                                    className="w-52 p-1.5 shadow-2xl border-slate-200 dark:border-slate-800"
                                  >
                                    <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-400 font-bold px-2 py-1.5">
                                      Request Actions
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />

                                    {(() => {
                                      const isPending =
                                        request.status === "pending";
                                      // Backend decides whose turn it is
                                      // (manager step, then the approving admin).
                                      const canApproveReject =
                                        isPending && request.can_act;
                                      const canCancel =
                                        canEditLeave && canCancelLeave(request);
                                      const canEditRequest =
                                        canEditLeave &&
                                        (request.status === "approved" ||
                                          request.status === "pending");
                                      const hasAttachment =
                                        !!request.document_key;

                                      const hasAnyAction =
                                        isPending ||
                                        canCancel ||
                                        canEditRequest ||
                                        hasAttachment;

                                      return (
                                        <>
                                          {/* Approve / Reject */}
                                          {canApproveReject && (
                                            <>
                                              <DropdownMenuItem
                                                className="text-emerald-600 cursor-pointer text-xs font-bold focus:text-emerald-700 focus:bg-emerald-50/50 rounded-md py-2.5 px-2.5 mb-0.5 group/item"
                                                onClick={() => {
                                                  if (!canEditLeave) return;
                                                  openApprovalDialog(request);
                                                }}
                                                disabled={
                                                  actionLoading || !canEditLeave
                                                }
                                              >
                                                <div className="w-7 h-7 rounded-full bg-emerald-50 group-focus/item:bg-emerald-100 flex items-center justify-center mr-3 transition-colors">
                                                  <CheckCircle2 className="w-4 h-4" />
                                                </div>
                                                Approve Leave
                                              </DropdownMenuItem>

                                              <DropdownMenuItem
                                                className="text-rose-600 cursor-pointer text-xs font-bold focus:text-rose-700 focus:bg-rose-50/50 rounded-md py-2.5 px-2.5 group/item"
                                                onClick={() => {
                                                  if (!canEditLeave) return;
                                                  openRejectionDialog(request);
                                                }}
                                                disabled={
                                                  actionLoading || !canEditLeave
                                                }
                                              >
                                                <div className="w-7 h-7 rounded-full bg-rose-50 group-focus/item:bg-rose-100 flex items-center justify-center mr-3 transition-colors">
                                                  <XCircle className="w-4 h-4" />
                                                </div>
                                                Reject Leave
                                              </DropdownMenuItem>
                                            </>
                                          )}

                                          {isPending && !request.can_act && (
                                            <DropdownMenuItem
                                              disabled
                                              className="text-xs rounded-md py-2.5 px-2.5"
                                            >
                                              <div className="w-7 h-7 rounded-full bg-amber-50 flex items-center justify-center mr-3">
                                                <Clock className="w-4 h-4 text-amber-600" />
                                              </div>
                                              {request.approval_stage === "manager"
                                                ? `Waiting for ${request.awaiting_manager?.name || "the manager"}`
                                                : "Awaiting admin approval"}
                                            </DropdownMenuItem>
                                          )}

                                          {canEditRequest && (
                                            <DropdownMenuItem
                                              className="text-slate-700 dark:text-slate-300 cursor-pointer text-xs font-bold focus:bg-slate-100 dark:focus:bg-slate-800 rounded-md py-2.5 px-2.5 group/item"
                                              onClick={() => {
                                                if (!canEditLeave) return;
                                                openEditDialog(request);
                                              }}
                                            >
                                              <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 group-focus/item:bg-slate-200 dark:group-focus/item:bg-slate-700 flex items-center justify-center mr-3 transition-colors">
                                                <Pencil className="w-4 h-4" />
                                              </div>
                                              Edit Leave
                                            </DropdownMenuItem>
                                          )}

                                          {/* Cancel */}
                                          {!isPending && canCancel && (
                                            <DropdownMenuItem
                                              className="text-slate-700 dark:text-slate-300 cursor-pointer text-xs font-bold focus:bg-slate-100 dark:focus:bg-slate-800 rounded-md py-2.5 px-2.5 group/item"
                                              onClick={() => {
                                                if (!canEditLeave) return;
                                                openCancelDialog(request);
                                              }}
                                            >
                                              <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 group-focus/item:bg-slate-200 dark:group-focus/item:bg-slate-700 flex items-center justify-center mr-3 transition-colors">
                                                <Ban className="w-4 h-4" />
                                              </div>
                                              Cancel Leave
                                            </DropdownMenuItem>
                                          )}

                                          {/* View Attachment */}
                                          {hasAttachment && (
                                            <DropdownMenuItem
                                              onClick={() =>
                                                viewLeaveDocument(
                                                  request.document_key,
                                                )
                                              }
                                              className="
            text-[#3194a0] dark:text-[#4ec0c9]
            cursor-pointer text-xs font-bold
            focus:text-[#1f6f78]
            focus:bg-[#3194a0]/10
            dark:focus:bg-[#3194a0]/20
            rounded-md py-2.5 px-2.5
            group/item transition-colors
          "
                                            >
                                              <div
                                                className="
              w-7 h-7 rounded-full
              bg-[#3194a0]/10 dark:bg-[#3194a0]/20
              group-focus/item:bg-[#3194a0]/20
              flex items-center justify-center mr-3
              transition-colors
            "
                                              >
                                                <File className="w-4 h-4" />
                                              </div>
                                              View Attachment
                                            </DropdownMenuItem>
                                          )}

                                          {/* No Actions */}
                                          {!hasAnyAction && (
                                            <div className="px-2 py-3 flex items-center justify-center bg-slate-50/50 dark:bg-slate-800/30 rounded-md border border-dashed border-slate-200 dark:border-slate-700 my-1">
                                              <span className="text-[11px] text-slate-400 font-medium italic flex items-center gap-1.5">
                                                <Info className="w-3 h-3" />
                                                No pending actions
                                              </span>
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
              </div>
              {/* Pagination Controls */}
              {filteredRequests.length > itemsPerPage && (
                <div className="flex items-center justify-end sm:justify-between gap-3 pt-6">
                  <PaginationPageSize value={itemsPerPage} onValueChange={handlePageSizeChange} />
                  {/* Page Info */}
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Page {leavePage} of {leaveTotalPages}
                  </p>

                  <div className="flex space-x-2">
                    {/* Prev */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        leavePage > 1 && setLeavePage(leavePage - 1)
                      }
                      disabled={leavePage === 1}
                    >
                      Previous
                    </Button>

                    {/* Page Numbers */}
                    {visibleLeavePages.map((page) => {
                      if (typeof page === "string") {
                        return (
                          <span
                            key={page}
                            className="flex h-9 items-center px-1 text-sm text-gray-500 dark:text-gray-400"
                          >
                            ...
                          </span>
                        );
                      }

                      return (
                        <Button
                          key={page}
                          variant={leavePage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setLeavePage(page)}
                        >
                          {page}
                        </Button>
                      );
                    })}

                    {/* Next */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        leavePage < leaveTotalPages &&
                        setLeavePage(leavePage + 1)
                      }
                      disabled={leavePage === leaveTotalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Empty State */}
            {filteredRequests.length === 0 && (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  No Leave Requests Found
                </h3>
                <p className="text-gray-500">
                  {searchTerm || statusFilter !== "all"
                    ? "Try adjusting your search or filter criteria."
                    : "Leave requests from employees will appear here."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </TooltipProvider>

      <Card>
        <CardHeader className="px-6 py-4 dark:border-gray-700">
          <CardTitle className="text-xl">Leave Encashment</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="overflow-x-auto">
            <div className="border border-border rounded-lg overflow-hidden">
              <Table className="w-full">
                <TableHeader>
                  <TableRow className="border-b border-border">
                    <TableHead className="text-muted-foreground min-w-[220px]">
                      Employee
                    </TableHead>

                    <TableHead className="text-muted-foreground min-w-[120px]">
                      Days
                    </TableHead>

                    <TableHead className="text-muted-foreground min-w-[180px]">
                      Amount / Day
                    </TableHead>

                    <TableHead className="text-muted-foreground min-w-[180px]">
                      Estimated Amount
                    </TableHead>

                    <TableHead className="text-muted-foreground min-w-[160px]">
                      Requested On
                    </TableHead>

                    <TableHead className="text-muted-foreground w-[150px]">
                      Status
                    </TableHead>

                    <TableHead className="text-muted-foreground text-center w-[190px]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {paginatedEncashRequests.map((request, idx) => {
                    const employeeName =
                      request.employee_id?.user_id?.name || "Unknown";
                    const employeeId = request.employee_id?.employee_id || "--";

                    return (
                      <TableRow
                        key={request.id}
                        className={[
                          "border-b border-border",
                          "transition-colors",
                          "hover:bg-muted/50",
                        ].join(" ")}
                      >
                        {/* Employee */}
                        <TableCell className="align-middle">
                          <div className="min-w-0">
                            <div className="font-semibold text-sm truncate">
                              {employeeName}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {employeeId}
                            </div>
                          </div>
                        </TableCell>

                        {/* Days */}
                        <TableCell className="align-middle">
                          <div className="inline-flex items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-primary/20 px-3 py-1 text-xs font-semibold tabular-nums">
                            {String(request.requested_days).padStart(2, "0")}d
                          </div>
                        </TableCell>

                        {/* Amount per day */}
                        <TableCell className="align-middle text-sm">
                          ₹{request.amount_per_day?.toLocaleString()}
                        </TableCell>

                        {/* Estimated */}
                        <TableCell className="align-middle text-sm font-semibold">
                          ₹{request.estimated_amount?.toLocaleString()}
                        </TableCell>

                        {/* Requested On */}
                        <TableCell className="align-middle text-sm">
                          {format(
                            new Date(request.requested_at),
                            "dd MMM yyyy",
                          )}
                        </TableCell>

                        {/* Status */}
                        <TableCell className="align-middle">
                          {getStatusBadge(request.status)}
                        </TableCell>

                        {/* Actions */}
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            {String(request.status).toLowerCase() ===
                            "pending" ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 px-3 text-xs font-semibold
    border-emerald-200 bg-emerald-50 text-emerald-700
    hover:bg-emerald-100
    dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300
    dark:hover:bg-emerald-950/50"
                                  onClick={() => {
                                    if (!canEditLeave) return;
                                    setSelectedEncashmentRequest(request);
                                    setApproveEncashDialogOpen(true);
                                  }}
                                  disabled={actionLoading || !canEditLeave}
                                >
                                  Approve
                                </Button>

                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 px-3 text-xs font-semibold
    border-rose-200 bg-rose-50 text-rose-700
    hover:bg-rose-100
    dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300
    dark:hover:bg-rose-950/50"
                                  onClick={() => {
                                    if (!canEditLeave) return;
                                    setSelectedEncashmentRequest(request);
                                    setRejectEncashDialogOpen(true);
                                  }}
                                  disabled={actionLoading || !canEditLeave}
                                >
                                  Reject
                                </Button>
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground italic">
                                No pending actions
                              </span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {encashmentRequest.length === 0 && (
              <div className="text-center py-8">
                <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  No Leave Encashment Requests
                </h3>
                <p className="text-gray-500">
                  Leave encashment requests from employees will appear here.
                </p>
              </div>
            )}

            {/* Pagination */}
            {encashmentRequest.length > itemsPerPage && (
              <div className="flex items-center justify-end sm:justify-between gap-3 pt-6">
                <PaginationPageSize value={itemsPerPage} onValueChange={handlePageSizeChange} />
                <p className="text-sm text-gray-500">
                  Page {encashPage} of {encashTotalPages}
                </p>

                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      encashPage > 1 && setEncashPage(encashPage - 1)
                    }
                    disabled={encashPage === 1}
                  >
                    Previous
                  </Button>

                  {[...Array(encashTotalPages)].map((_, i) => (
                    <Button
                      key={i}
                      variant={encashPage === i + 1 ? "default" : "outline"}
                      size="sm"
                      onClick={() => setEncashPage(i + 1)}
                    >
                      {i + 1}
                    </Button>
                  ))}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      encashPage < encashTotalPages &&
                      setEncashPage(encashPage + 1)
                    }
                    disabled={encashPage === encashTotalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="sm:max-w-[40vw]">
          <DialogHeader>
            <DialogTitle>Approve Leave Request</DialogTitle>
            <CardDescription>
              {selectedRequest?.approval_stage === "manager"
                ? "Your approval sends this request to an admin for the final approval."
                : "Confirm approval for the selected request."}
            </CardDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4 pt-2">
              <div className="p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 space-y-1">
                <p className="text-sm">
                  Employee: <strong>{selectedRequest.employee_name}</strong>
                </p>
                <p className="text-sm">
                  Leave: <strong>{selectedRequest.leave_type}</strong> for{" "}
                  <strong className="text-primary">
                    {selectedRequest.days} days
                  </strong>
                </p>
                <p className="text-sm">
                  Reason:{" "}
                  <span className=" text-gray-600 dark:text-gray-400">
                    {selectedRequest.reason}
                  </span>
                </p>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Approval Comment (Optional)
                </div>
                <Textarea
                  value={approvalComment}
                  onChange={(e) => setApprovalComment(e.target.value)}
                  placeholder="Add any comments"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowApprovalDialog(false);
                setSelectedRequest(null);
                setApprovalComment("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!canEditLeave) return;
                handleApproveReject(
                  selectedRequest.id,
                  "approved",
                  approvalComment,
                );
              }}
              disabled={actionLoading}
              className=""
            >
              <Send className="h-4 w-4 mr-2" />
              {actionLoading
                ? "Approving..."
                : selectedRequest?.approval_stage === "manager"
                  ? "Approve & Send to Admin"
                  : "Approve Leave"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={approveEncashDialogOpen}
        onOpenChange={setApproveEncashDialogOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approve Leave Encashment</DialogTitle>
            <DialogDescription>
              This action will approve the leave encashment and push it into
              payroll.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span>Days Requested</span>
              <span className="font-semibold">
                {selectedEncashmentRequest?.requested_days}
              </span>
            </div>

            <div className="flex justify-between">
              <span>Estimated Amount</span>
              <span className="font-semibold">
                ₹{selectedEncashmentRequest?.estimated_amount?.toLocaleString()}
              </span>
            </div>

            {/* 🔥 PAYOUT DATE */}
            <div className="space-y-1 pt-2">
              <Label>Payout Date *</Label>
              <DatePickerInput
                value={payoutDay}
                onChange={setPayoutDay}
                placeholder="Select payout date"
                minDate={new Date()} // optional: prevent past dates
              />
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() => setApproveEncashDialogOpen(false)}
            >
              Cancel
            </Button>

            <Button
              onClick={approveEncashmentRequest}
              disabled={actionLoading || !canEditLeave}
            >
              {actionLoading ? "Approving..." : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={rejectEncashDialogOpen}
        onOpenChange={setRejectEncashDialogOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Leave Encashment</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this encashment request.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span>Days Requested</span>
                <span className="font-semibold">
                  {selectedEncashmentRequest?.requested_days}
                </span>
              </div>

              <div className="flex justify-between">
                <span>Estimated Amount</span>
                <span className="font-semibold">
                  ₹
                  {selectedEncashmentRequest?.estimated_amount?.toLocaleString()}
                </span>
              </div>
            </div>

            <div>
              <Label htmlFor="rejection_reason">Rejection Reason *</Label>
              <Textarea
                id="rejection_reason"
                rows={3}
                value={encashRejectionReason}
                onChange={(e) => setEncashRejectionReason(e.target.value)}
                placeholder="Explain why this request is being rejected"
              />
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setRejectEncashDialogOpen(false);
                setRejectionReason("");
              }}
            >
              Cancel
            </Button>

            <Button
              variant="destructive"
              disabled={!encashRejectionReason}
              onClick={rejectEncashmentRequest}
            >
              {actionLoading ? "Rejecting..." : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={showRejectionDialog} onOpenChange={setShowRejectionDialog}>
        <DialogContent className="sm:max-w-[40vw]">
          <DialogHeader>
            <DialogTitle>Reject Leave Request</DialogTitle>
            <CardDescription>A rejection reason is required.</CardDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4 pt-2">
              <div className="p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 space-y-1">
                <p className="text-sm">
                  Employee: <strong>{selectedRequest.employee_name}</strong>
                </p>
                <p className="text-sm">
                  Leave: <strong>{selectedRequest.leave_type}</strong> for{" "}
                  <strong className="text-red-600">
                    {selectedRequest.days} days
                  </strong>
                </p>
                <p className="text-sm">
                  Reason:{" "}
                  <span className=" text-gray-600 dark:text-gray-400">
                    {selectedRequest.reason}
                  </span>
                </p>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1 text-gray-700 dark:text-gray-300">
                  Rejection Reason *
                </label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Write reason..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectionDialog(false);
                setSelectedRequest(null);
                setRejectionReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                handleApproveReject(
                  selectedRequest.id,
                  "rejected",
                  rejectionReason,
                )
              }
              disabled={actionLoading || !rejectionReason.trim()}
            >
              <Send className="h-4 w-4 mr-2" />
              {actionLoading ? "Rejecting..." : "Reject Leave"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md p-6 shadow-2xl">
          <DialogHeader className="space-y-1">
            <div className="flex items-center space-x-3">
              <Pencil className="h-6 w-6 text-slate-600 dark:text-slate-300 flex-shrink-0" />
              <DialogTitle className="text-2xl font-bold">
                Edit Leave
              </DialogTitle>
            </div>
            <p className="text-sm text-muted-foreground pt-1">
              {editingLeave?.employee_name} &middot;{" "}
              {editingLeave?.start_date &&
                format(new Date(editingLeave.start_date), "dd MMM yyyy")}
              {editingLeave &&
                !isSingleDayLeave(editingLeave) &&
                editingLeave.end_date &&
                ` – ${format(new Date(editingLeave.end_date), "dd MMM yyyy")}`}
            </p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium">Leave Type</Label>
              <Select
                value={editForm.leave_id}
                onValueChange={(value) =>
                  setEditForm((prev) => ({ ...prev, leave_id: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveConfig.map((type) => (
                    <SelectItem key={type._id} value={String(type._id)}>
                      {type.leave_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isSingleDayLeave(editingLeave) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit_half_day"
                  checked={editForm.half_day}
                  onCheckedChange={(checked) =>
                    setEditForm((prev) => ({
                      ...prev,
                      half_day: Boolean(checked),
                    }))
                  }
                />
                <Label htmlFor="edit_half_day" className="text-sm font-medium">
                  Half Day
                </Label>
              </div>
            )}

            {editForm.half_day && isSingleDayLeave(editingLeave) && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">
                    Working Start Time
                  </Label>
                  <Input
                    type="time"
                    value={editForm.half_day_start_time}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        half_day_start_time: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">
                    Working End Time
                  </Label>
                  <Input
                    type="time"
                    value={editForm.half_day_end_time}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        half_day_end_time: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium">Comment (optional)</Label>
              <Textarea
                rows={3}
                value={editForm.admin_comment}
                placeholder="Why is this leave being changed?"
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    admin_comment: e.target.value,
                  }))
                }
              />
            </div>

            {editValidationError && (
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                {editValidationError}
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              disabled={editSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditLeave}
              disabled={editSubmitting || !!editValidationError}
            >
              {editSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-md p-6 shadow-2xl">
          {/* Enhanced shadow/border */}
          <DialogHeader className="space-y-1">
            <div className="flex items-center space-x-3">
              <XCircle className="h-7 w-7 text-red-600 dark:text-red-400 flex-shrink-0" />
              <DialogTitle className="text-2xl font-bold text-red-700 dark:text-red-400">
                Confirm Cancellation
              </DialogTitle>
            </div>
            <CardDescription className="pt-1">
              You are about to cancel this leave request. Please confirm the
              details below.
            </CardDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Summary of Leave being Cancelled (Improved Visuals) */}
            {cancellingLeave && (
              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wider">
                  Request Details
                </h4>
                <div className="space-y-1">
                  <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                    {cancellingLeave.leave_name}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Dates:</span>{" "}
                    {format(
                      new Date(cancellingLeave.start_date),
                      "dd MMM yyyy",
                    )}
                    {cancellingLeave.start_date !== cancellingLeave.end_date &&
                      ` - ${format(
                        new Date(cancellingLeave.end_date),
                        "dd MMM yyyy",
                      )}`}
                    &nbsp;({cancellingLeave.days} days)
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-2">
                    <span className="font-medium">Current Status:</span>
                    <Badge variant="secondary" className="capitalize">
                      {cancellingLeave.status}
                    </Badge>
                  </p>
                </div>
              </div>
            )}

            {/* Cancellation Reason Input */}
            <div>
              <Label
                htmlFor="cancellation_reason"
                className="text-sm font-medium"
              >
                Reason for Cancellation <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="cancellation_reason"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="E.g., Travel plans changed, feeling better now, shifted dates."
                rows={4}
                className="dark:text-gray-200 dark:placeholder:text-gray-500"
                required
              />
            </div>
          </div>
          {/* Action Buttons (DialogFooter) */}
          <DialogFooter className="flex-col sm:flex-row sm:justify-between pt-4">
            {" "}
            {/* Changed to justify-between for better spacing */}
            <Button
              onClick={() => setShowCancelDialog(false)}
              variant="outline"
              disabled={cancelling}
              className="w-full sm:w-auto order-2 sm:order-1 mt-2 sm:mt-0"
            >
              Go Back
            </Button>
            <Button
              onClick={handleCancelLeave}
              disabled={cancelling || !cancellationReason.trim()}
              className="w-full sm:w-auto flex items-center space-x-2 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 order-1 sm:order-2"
            >
              {cancelling ? (
                <>
                  <Clock className="h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <X className="h-4 w-4" />
                  <span>Cancel Leave</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showLeaveDetailsDialog}
        onOpenChange={setShowLeaveDetailsDialog}
      >
        <DialogContent className="max-w-3xl gap-0 p-0 overflow-hidden rounded-xl border border-slate-200 shadow-2xl flex flex-col max-h-[90vh]">
          {/* Header: Smaller & Subtler */}
          <div className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800 p-4 relative flex-shrink-0">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-xs font-bold text-white-300 border border-slate-300 dark:border-slate-700">
                {selectedRequest &&
                  selectedRequest.employee_name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-slate-900 dark:text-white leading-tight">
                  {selectedRequest && selectedRequest.employee_name}
                </DialogTitle>
                <DialogDescription className="text-slate-500 text-xs mt-0.5">
                  {selectedRequest?.leave_type} Request •{" "}
                  {selectedRequest?.days} Days
                </DialogDescription>
              </div>
              <div className="ml-auto mr-8">
                {selectedRequest && getLeaveStatus(selectedRequest)}
              </div>
            </div>
          </div>

          {/* Scrollable Body */}
          <div className="p-6 space-y-4 bg-slate-100/10 dark:bg-slate-950 overflow-y-auto flex-grow">
            {/* Primary Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-3 rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-100/10">
              <InfoItem
                label="Emp ID"
                value={selectedRequest?.emp_id}
                icon={User}
              />
              <InfoItem
                label="Code"
                value={selectedRequest?.leave_code}
                icon={Hash}
              />
              <InfoItem
                label="Half Day"
                value={
                  selectedRequest?.half_day
                    ? selectedRequest?.half_day_start_time &&
                      selectedRequest?.half_day_end_time
                      ? `Yes (${selectedRequest.half_day_start_time} – ${selectedRequest.half_day_end_time})`
                      : "Yes"
                    : "No"
                }
                icon={Clock}
              />
              <InfoItem
                label="Applied"
                value={
                  selectedRequest &&
                  format(new Date(selectedRequest.applied_date), "MMM dd, yyyy")
                }
                icon={Calendar}
              />
            </div>

            {/* Dates Section */}
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Duration
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-50 dark:border-slate-900 bg-white dark:bg-slate-900 shadow-sm">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center text-indigo-600 border border-indigo-100 dark:border-indigo-900/30">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[9px] font-bold text-slate-400 uppercase">
                      Start Date
                    </p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
                      {selectedRequest &&
                        format(
                          new Date(selectedRequest.start_date),
                          "EEE, MMM dd",
                        )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-50 dark:border-slate-900 bg-white dark:bg-slate-900 shadow-sm">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center text-indigo-600 border border-indigo-100 dark:border-indigo-900/30">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[9px] font-bold text-slate-400 uppercase">
                      End Date
                    </p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
                      {selectedRequest &&
                        format(
                          new Date(selectedRequest.end_date),
                          "EEE, MMM dd",
                        )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline Section */}
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Processing Timeline
              </h4>
              <div className="space-y-4 border-l-2 border-slate-100 dark:border-slate-800 ml-1.5 pl-5 relative">
                <div className="relative">
                  <div className="absolute -left-[25px] top-1 w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-700 border-2 border-white dark:border-slate-950" />
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">
                    Applied
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    {selectedRequest &&
                      format(
                        new Date(selectedRequest.applied_date),
                        "MMM dd, yyyy 'at' p",
                      )}
                  </p>
                </div>

                {selectedRequest?.manager_approval?.approved_at && (
                  <div className="relative">
                    <div className="absolute -left-[25px] top-1 w-2 h-2 rounded-full bg-emerald-400 border-2 border-white dark:border-slate-950" />
                    <p className="text-[9px] font-bold text-emerald-600 dark:text-emerald-500 uppercase tracking-tight">
                      Approved by manager
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      By{" "}
                      <span className="font-semibold text-slate-800 dark:text-slate-200">
                        {selectedRequest.manager_approval.approved_by}
                      </span>{" "}
                      on{" "}
                      {format(
                        new Date(selectedRequest.manager_approval.approved_at),
                        "MMM dd 'at' p",
                      )}
                    </p>
                    {selectedRequest.manager_approval.comment && (
                      <p className="text-xs italic text-slate-500 mt-0.5">
                        "{selectedRequest.manager_approval.comment}"
                      </p>
                    )}
                  </div>
                )}

                {selectedRequest?.approved_by && (
                  <div className="relative">
                    <div className="absolute -left-[25px] top-1 w-2 h-2 rounded-full bg-emerald-500 border-2 border-white dark:border-slate-950" />
                    <p className="text-[9px] font-bold text-emerald-600 dark:text-emerald-500 uppercase tracking-tight">
                      Approved
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      By{" "}
                      <span className="font-semibold text-slate-800 dark:text-slate-200">
                        {selectedRequest.approved_by}
                      </span>{" "}
                      on{" "}
                      {format(
                        new Date(selectedRequest.approved_date),
                        "MMM dd 'at' p",
                      )}
                    </p>
                  </div>
                )}

                {selectedRequest?.rejected_by && (
                  <div className="relative">
                    <div className="absolute -left-[25px] top-1 w-2 h-2 rounded-full bg-rose-500 border-2 border-white dark:border-slate-950" />
                    <p className="text-[9px] font-bold text-rose-600 dark:text-rose-500 uppercase tracking-tight">
                      Rejected
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      By{" "}
                      <span className="font-semibold text-slate-800 dark:text-slate-200">
                        {selectedRequest.rejected_by}
                      </span>{" "}
                      on{" "}
                      {format(
                        new Date(selectedRequest.rejected_date),
                        "MMM dd 'at' p",
                      )}
                    </p>
                  </div>
                )}

                {selectedRequest?.cancelled_by && (
                  <div className="relative">
                    <div className="absolute -left-[25px] top-1 w-2 h-2 rounded-full bg-amber-500 border-2 border-white dark:border-slate-950" />
                    <p className="text-[9px] font-bold text-amber-600 dark:text-amber-500 uppercase tracking-tight">
                      Cancelled
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      By{" "}
                      <span className="font-semibold text-slate-800 dark:text-slate-200">
                        {selectedRequest.cancelled_by}
                      </span>{" "}
                      on{" "}
                      {format(
                        new Date(selectedRequest.cancelled_date),
                        "MMM dd 'at' p",
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Remarks */}
            {(selectedRequest?.reason || selectedRequest?.admin_comment) && (
              <div className="space-y-3">
                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <MessageSquare className="w-3 h-3" /> Remarks & Notes
                </h4>
                <div className="space-y-3">
                  {selectedRequest?.reason && (
                    <NoteItem
                      title="Employee's Reason"
                      text={selectedRequest.reason}
                    />
                  )}
                  {selectedRequest?.admin_comment && (
                    <NoteItem
                      title="Reviewer Feedback"
                      text={selectedRequest.admin_comment}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="p-3 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowLeaveDetailsDialog(false)}
              className="text-xs h-8"
            >
              Close
            </Button>
            {selectedRequest?.status === "pending" && selectedRequest?.can_act && (
              <Button
                size="sm"
                className="bg-slate-900 hover:bg-slate-800 text-xs h-8"
                onClick={() => {
                  setShowLeaveDetailsDialog(false);
                  openApprovalDialog(selectedRequest);
                }}
              >
                Approve Leave
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ADMIN APPLY LEAVE DIALOG */}
      <ApplyLeaveDialog
        isOpen={showAdminApplyDialog}
        setIsOpen={setShowAdminApplyDialog}
        employees={employees} // Pass full employee list for selection
        leaveEntitlement={leaveStats} // Pass all employee entitlements
        leaveTypes={[]} // Pass all available leave types
        workingDaysConfig={workingDaysConfig}
        holidays={holidays}
        isEmployeeView={false}
        onSubmit={handleAdminLeaveSubmit}
      />
    </div>
  );
};

export default LeaveManagement;
